export const FAILURE_CODES = {
  policy: ["content_blocked"],
  network: ["dns_failed", "connection_refused", "connection_reset", "network_unreachable", "tls_failed", "network_failed"],
  timeout: ["connect_timeout", "request_timeout", "media_processing_timeout"],
  rate_limit: ["rate_limited"],
  authentication: ["authentication_failed"],
  authorization: ["permission_denied"],
  provider: ["resource_not_found", "provider_unavailable", "request_rejected", "attempts_exhausted"],
  media: ["media_upload_failed", "media_processing_failed", "media_too_large", "unsupported_media"],
  response: ["output_truncated", "empty_response", "malformed_response"],
  pipeline: ["serial_parse_failed", "source_mismatch", "pipeline_mismatch"],
  validation: ["script_validation_failed"],
  cancelled: ["cancelled"],
  unknown: ["unknown", "legacy_unclassified"],
} as const;

export type FailureCodeByType = {
  [Type in keyof typeof FAILURE_CODES]: (typeof FAILURE_CODES)[Type][number];
};

export type FailureType = keyof typeof FAILURE_CODES;

export type FailureClassification = {
  [Type in FailureType]: {
    failure_type: Type;
    failure_code: FailureCodeByType[Type];
  };
}[FailureType];

export type GeminiPolicySource = "prompt" | "candidate";

export type GeminiPolicyReason =
  | "SAFETY"
  | "PROHIBITED_CONTENT"
  | "BLOCKLIST"
  | "SPII"
  | "RECITATION"
  | "IMAGE_SAFETY"
  | "IMAGE_PROHIBITED_CONTENT"
  | "IMAGE_RECITATION"
  | "ESCALATION"
  | "OTHER"
  | "UNSPECIFIED"
  | "UNKNOWN";

export interface ProviderSafetyRating {
  category: string;
  probability: string;
  blocked: boolean;
}

export interface FailureContext {
  provider?: string;
  model?: string;
  operation?: string;
  http_status?: number;
  system_code?: string;
  provider_source?: GeminiPolicySource;
  provider_reason?: GeminiPolicyReason;
  provider_reason_raw?: string;
  finish_message?: string;
  safety_ratings?: ProviderSafetyRating[];
  details?: string[];
}

export type OperationalFailure = FailureClassification & FailureContext & {
  retryable: boolean;
  message: string;
};

function oneOf<const Values extends readonly string[]>(value: unknown, values: Values): value is Values[number] {
  return typeof value === "string" && values.some((candidate) => candidate === value);
}

export function parseFailureClassification(type: unknown, code: unknown): FailureClassification | null {
  switch (type) {
    case "policy": return oneOf(code, FAILURE_CODES.policy) ? { failure_type: type, failure_code: code } : null;
    case "network": return oneOf(code, FAILURE_CODES.network) ? { failure_type: type, failure_code: code } : null;
    case "timeout": return oneOf(code, FAILURE_CODES.timeout) ? { failure_type: type, failure_code: code } : null;
    case "rate_limit": return oneOf(code, FAILURE_CODES.rate_limit) ? { failure_type: type, failure_code: code } : null;
    case "authentication": return oneOf(code, FAILURE_CODES.authentication) ? { failure_type: type, failure_code: code } : null;
    case "authorization": return oneOf(code, FAILURE_CODES.authorization) ? { failure_type: type, failure_code: code } : null;
    case "provider": return oneOf(code, FAILURE_CODES.provider) ? { failure_type: type, failure_code: code } : null;
    case "media": return oneOf(code, FAILURE_CODES.media) ? { failure_type: type, failure_code: code } : null;
    case "response": return oneOf(code, FAILURE_CODES.response) ? { failure_type: type, failure_code: code } : null;
    case "pipeline": return oneOf(code, FAILURE_CODES.pipeline) ? { failure_type: type, failure_code: code } : null;
    case "validation": return oneOf(code, FAILURE_CODES.validation) ? { failure_type: type, failure_code: code } : null;
    case "cancelled": return oneOf(code, FAILURE_CODES.cancelled) ? { failure_type: type, failure_code: code } : null;
    case "unknown": return oneOf(code, FAILURE_CODES.unknown) ? { failure_type: type, failure_code: code } : null;
    default: return null;
  }
}

export function createOperationalFailure(
  classification: FailureClassification,
  fields: FailureContext & { retryable: boolean; message: string },
): OperationalFailure {
  return { ...classification, ...fields };
}

export function classificationOf(failure: OperationalFailure): FailureClassification {
  switch (failure.failure_type) {
    case "policy": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "network": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "timeout": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "rate_limit": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "authentication": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "authorization": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "provider": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "media": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "response": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "pipeline": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "validation": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "cancelled": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
    case "unknown": return { failure_type: failure.failure_type, failure_code: failure.failure_code };
  }
}

export function contextOf(failure: OperationalFailure): FailureContext {
  return {
    ...(failure.provider !== undefined ? { provider: failure.provider } : {}),
    ...(failure.model !== undefined ? { model: failure.model } : {}),
    ...(failure.operation !== undefined ? { operation: failure.operation } : {}),
    ...(failure.http_status !== undefined ? { http_status: failure.http_status } : {}),
    ...(failure.system_code !== undefined ? { system_code: failure.system_code } : {}),
    ...(failure.provider_source !== undefined ? { provider_source: failure.provider_source } : {}),
    ...(failure.provider_reason !== undefined ? { provider_reason: failure.provider_reason } : {}),
    ...(failure.provider_reason_raw !== undefined ? { provider_reason_raw: failure.provider_reason_raw } : {}),
    ...(failure.finish_message !== undefined ? { finish_message: failure.finish_message } : {}),
    ...(failure.safety_ratings !== undefined ? { safety_ratings: failure.safety_ratings } : {}),
    ...(failure.details !== undefined ? { details: failure.details } : {}),
  };
}

export function aggregateFailures(failures: OperationalFailure[], message: string): OperationalFailure {
  if (failures.length === 0) {
    return createOperationalFailure(
      { failure_type: "unknown", failure_code: "unknown" },
      { retryable: false, message },
    );
  }
  const first = failures[0];
  const sameClassification = failures.every(
    (failure) => failure.failure_type === first.failure_type && failure.failure_code === first.failure_code,
  );
  const classification: FailureClassification = sameClassification
    ? classificationOf(first)
    : { failure_type: "provider", failure_code: "attempts_exhausted" };
  return createOperationalFailure(classification, {
    retryable: failures.some((failure) => failure.retryable),
    message,
  });
}

export function formatFailure(failure: OperationalFailure): string {
  const route = failure.provider || failure.model
    ? `${failure.provider ?? "provider"}/${failure.model ?? "model"} `
    : "";
  const reason = failure.provider_reason ? ` provider_reason=${failure.provider_reason}` : "";
  const system = failure.system_code ? ` system_code=${failure.system_code}` : "";
  const status = failure.http_status !== undefined ? ` http_status=${failure.http_status}` : "";
  const details = failure.details?.length ? ` | ${failure.details.join(" | ")}` : "";
  return `${route}${failure.failure_type}/${failure.failure_code}${reason}${system}${status}: ${failure.message}${details}`;
}
